import { Eye, EyeOff } from 'lucide-react'
import { type ComponentProps, type KeyboardEvent, type ReactNode, useId, useState } from 'react'

import { fieldBoxClasses } from '../lib/classes'
import { cn } from '../lib/cn'
import { useFieldControl } from './form-field'

export interface InputProps extends ComponentProps<'input'> {
  /** Ícone decorativo à esquerda (lucide). */
  startIcon?: ReactNode
  /** Elemento interativo à direita, dentro da caixa (ex.: botão de mostrar senha). */
  endSlot?: ReactNode
  /** Força o estado inválido fora de um FormField. */
  invalid?: boolean
}

/**
 * Campo de texto. `className` vai na caixa visível (largura, margens); as demais props vão
 * no `<input>`, inclusive `ref`. Dentro de um FormField, id e aria-* chegam sozinhos.
 */
export function Input({
  className,
  startIcon,
  endSlot,
  invalid,
  type = 'text',
  id,
  disabled,
  required,
  'aria-describedby': describedBy,
  ...props
}: InputProps) {
  const control = useFieldControl({
    id,
    disabled,
    required,
    invalid,
    'aria-describedby': describedBy,
  })

  return (
    <div
      data-slot="input"
      data-invalid={control.invalid || undefined}
      data-disabled={control.disabled || undefined}
      className={cn(
        fieldBoxClasses,
        'has-[input:focus-visible]:bg-surface has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-petroleo',
        className,
      )}
    >
      {startIcon ? (
        <span aria-hidden className="pointer-events-none flex shrink-0 text-ink-2 [&_svg]:size-4">
          {startIcon}
        </span>
      ) : null}
      <input
        type={type}
        id={control.id}
        disabled={control.disabled}
        {...control.ariaProps}
        className="h-full w-full min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-ink-3 focus-visible:outline-none disabled:cursor-not-allowed"
        {...props}
      />
      {endSlot}
    </div>
  )
}

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'endSlot'> {
  /** Rótulo do botão de alternância (o estado vai em aria-pressed). */
  toggleLabel?: string
}

/** Senha com botão de mostrar/ocultar. Nunca perde o que foi digitado ao alternar. */
export function PasswordInput({
  toggleLabel = 'Mostrar senha',
  autoComplete = 'current-password',
  disabled,
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false)

  return (
    <Input
      {...props}
      disabled={disabled}
      type={visible ? 'text' : 'password'}
      autoComplete={autoComplete}
      endSlot={
        <button
          type="button"
          aria-label={toggleLabel}
          aria-pressed={visible}
          disabled={disabled}
          onClick={() => setVisible((current) => !current)}
          className="-mr-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-ink-2 hover:bg-surface hover:text-ink disabled:pointer-events-none [&_svg]:size-4"
        >
          {visible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
        </button>
      }
    />
  )
}

export interface TextareaProps extends ComponentProps<'textarea'> {
  invalid?: boolean
  /**
   * Contador de caracteres abaixo do campo ("12 / 500" com `maxLength`, "12 caracteres" sem).
   * Abaixo de `minLength`, mostra quantos faltam.
   */
  showCount?: boolean
  /** Ctrl+Enter (⌘+Enter no Mac) dentro do campo: salvar sem tirar a mão do teclado. */
  onSubmitShortcut?: (event: KeyboardEvent<HTMLTextAreaElement>) => void
}

export function Textarea({
  className,
  invalid,
  id,
  disabled,
  required,
  showCount = false,
  onSubmitShortcut,
  onKeyDown,
  onChange,
  value,
  defaultValue,
  maxLength,
  minLength,
  'aria-describedby': describedBy,
  ...props
}: TextareaProps) {
  const counterId = useId()
  const [uncontrolledLength, setUncontrolledLength] = useState(() =>
    defaultValue === undefined ? 0 : String(defaultValue).length,
  )
  const length = value === undefined ? uncontrolledLength : String(value).length
  const control = useFieldControl({
    id,
    disabled,
    required,
    invalid,
    'aria-describedby':
      [describedBy, showCount ? counterId : null].filter(Boolean).join(' ') || undefined,
  })

  const textarea = (
    <textarea
      id={control.id}
      disabled={control.disabled}
      {...control.ariaProps}
      value={value}
      defaultValue={defaultValue}
      maxLength={maxLength}
      minLength={minLength}
      onChange={(event) => {
        if (value === undefined) setUncontrolledLength(event.target.value.length)
        onChange?.(event)
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event)
        if (
          onSubmitShortcut &&
          !event.defaultPrevented &&
          event.key === 'Enter' &&
          (event.ctrlKey || event.metaKey) &&
          !event.nativeEvent.isComposing
        ) {
          event.preventDefault()
          onSubmitShortcut(event)
        }
      }}
      className={cn(
        'min-h-20 w-full resize-y rounded-md border border-border bg-surface-2 px-3 py-2 text-body text-ink placeholder:text-ink-3',
        'focus-visible:bg-surface aria-invalid:border-framboesa',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...props}
    />
  )
  if (!showCount) return textarea

  const missing =
    minLength !== undefined && length > 0 && length < minLength ? minLength - length : 0
  const nearLimit = maxLength !== undefined && maxLength - length <= Math.max(10, maxLength * 0.05)
  return (
    <div className="flex w-full flex-col gap-1">
      {textarea}
      <p
        id={counterId}
        data-near-limit={nearLimit || undefined}
        className="flex justify-end text-micro text-ink-2 tabular-nums data-near-limit:font-medium data-near-limit:text-ink"
      >
        {missing > 0
          ? `Faltam ${missing} de ${minLength} caracteres`
          : maxLength !== undefined
            ? `${length} / ${maxLength}`
            : `${length} ${length === 1 ? 'caractere' : 'caracteres'}`}
      </p>
    </div>
  )
}
